//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { fetchHiredCandidates, getJobSeekersSuggestions, updateHiredCandidates } from '../../common/services/empMyJobsServices';
import { AppContext, LayoutContext } from '../../common/contexts';
import { PLACEHOLDER_SEARCH_NAME } from '../../common/placeholderHelper';
import { getFullNameByUser, getCityStateCountryText } from '../../common/textHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _pick from 'lodash/pick';
import _findIndex from 'lodash/findIndex';
import _map from 'lodash/map';
import _isEqual from 'lodash/isEqual';
import _slice from 'lodash/slice';
import { SAVE } from '../../common/constants';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    groupIcon: { color: 'rgba(17, 30, 20, 0.4)' },
    name: { fontWeight: 'bold' },
    deleteIconButton: { fontWeight: 'bold', color: 'rgba(17, 30, 20, 0.4)' },
    location: { color: 'rgba(17, 30, 20, 0.4)' },
    listContainer: { display: 'flex', padding: '10px 20px', alignItems: 'center' },
    profilePic: { marginRight: 20, height: 50, width: 50, borderRadius: 50 },
    jobSeekerContainer: { marginBottom: 16 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        jobSeekerOptions,
        setJobSeekerOptions,
        isDirty,
        // setIsDirty,
        handleSaveButton,
        // setSelectedOptions,
        handleChangeJobSeeker,
        esToken,
        setEsToken,
        // employerId,
    } = params;
    const { open, close } = props;

    const defns = {
        container: {
            open: open,
            onClose: close,
            label: 'Select Candidate Hired',
            actionButtonDefns: {
                cancel: { disabled: false, onClick: close },
                save: { onClick: handleSaveButton, disabled: !isDirty, label: SAVE, color: 'primary' },
            },
        },

        jobSeeker: {
            options: jobSeekerOptions,
            getOptionLabel: option => `${getFullNameByUser(option)}`,
            renderOption: option => (
                <div style={{ display: 'flex', padding: '10px 8px', alignItems: 'center' }}>
                    <AfitdAvatar src={option?.profilePicUrl} />
                    <div style={{ flex: 1, marginLeft: 8 }}>
                        <AfitdTypography style={{ fontWeight: 'bold' }}>{getFullNameByUser(option)}</AfitdTypography>
                        <AfitdTypography variant="body2" style={{ color: 'rgba(17, 30, 20, 0.4)' }}>
                            {getCityStateCountryText(option?.city, option?.state, option?.country)}
                        </AfitdTypography>
                    </div>
                </div>
            ),
            getOptionSelected: (option, value) => `${getFullNameByUser(option)}` === value,
            placeholder: PLACEHOLDER_SEARCH_NAME,
            onChange: handleChangeJobSeeker,
            onSuggestionsFetchRequested: async value => {
                const keywords = value ? value : undefined;
                const options = keywords ? await getJobSeekersSuggestions({ keywords, esToken, setEsToken }) : [];
                setJobSeekerOptions(options);
            },
        },

        name: { variant: 'body1' },
        location: { variant: 'body2' },
        deleteIconButton: {},
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function HiredDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { employerId, esToken, setEsToken } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { job, open, close, onHiredCandidatesSave } = props;
    const jobId = job?.uid;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [jobSeekerOptions, setJobSeekerOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [isInitList, setIsInitList] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    //Context

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     const unsubscribeOnSnapshot = fetchAllUsers(setUsers);

    //     // unsubscribe realtime update on unmount
    //     return () => {
    //         unsubscribeRealtimeUpdates([unsubscribeOnSnapshot]);
    //     };
    // }, []);
    useEffect(() => {
        const runFetchHiredCandidates = async () => {
            const hiredCandidates = await fetchHiredCandidates({ jobId });

            // update state only if the list is different
            setSelectedOptions(prev => (_isEqual(hiredCandidates, prev) ? prev : hiredCandidates));

            setIsInitList(true);
        };

        if (open && !isInitList) runFetchHiredCandidates();
    }, [open, isInitList, jobId]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleRemoveButton = idx => {
        setSelectedOptions(prev => [..._slice(prev, 0, idx), ..._slice(prev, idx + 1)]);
        setIsDirty(true);
    };

    const handleChangeJobSeeker = (evt, jobSeeker) => {
        // console.log('-------->', { job });
        if (jobSeeker) {
            const jobSeekerId = jobSeeker.uid;
            const dto = { ..._pick(jobSeeker, ['uid', 'firstName', 'middleName', 'lastName', 'profilePicUrl', 'country', 'state', 'city']) };
            setSelectedOptions(prev => {
                if (prev) {
                    const foundJsIdx = _findIndex(prev, item => item.uid === jobSeekerId);
                    if (foundJsIdx < 0) {
                        // js not already in the list, add it
                        return [{ ...dto }, ...prev];
                    } else {
                        // js already in the list, don't add it
                        return prev;
                    }
                } else {
                    return [{ ...dto }];
                }
            });
            setIsDirty(true);
        }
    };

    const handleSaveButton = async () => {
        const jsIdList = _map(selectedOptions, item => item.uid);
        const savedJsIdList = await updateHiredCandidates({ jsIdList, jobId, setSnackbarMessage, setSnackbarErrorMessage });

        savedJsIdList && onHiredCandidatesSave && onHiredCandidatesSave(savedJsIdList);

        close();
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles();
    const defns = getDefns({
        classes,
        props,
        jobSeekerOptions,
        setJobSeekerOptions,
        isDirty,
        setIsDirty,
        handleSaveButton,
        setSelectedOptions,
        handleChangeJobSeeker,
        esToken,
        setEsToken,
        employerId,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <AfitdEditDialog {...defns.container}>
                <div {...defns.jobSeekerContainer}>
                    <AfitdAutocomplete {...defns.jobSeeker} />
                </div>
                {selectedOptions?.map((el, idx) => (
                    <div key={getUniqueKey()} {...defns.listContainer}>
                        <AfitdAvatar src={el?.profilePicUrl} {...defns.profilePic} />
                        <div style={{ flex: 1 }}>
                            <AfitdTypography {...defns.name}>{getFullNameByUser(el)}</AfitdTypography>
                            <AfitdTypography {...defns.location}>{getCityStateCountryText(el.city, el.state, el.country)}</AfitdTypography>
                        </div>
                        <IconButton {...defns.deleteIconButton} onClick={() => handleRemoveButton(idx)}>
                            <DeleteIcon />
                        </IconButton>
                    </div>
                ))}
            </AfitdEditDialog>
        </div>
    );
}
